import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ChevronLeft, Download, FileSpreadsheet, Calendar,
  Loader2, Database, RefreshCw, CheckCircle2, XCircle, AlertCircle, ScrollText, ShieldAlert, ChevronRight,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function isoToday() {
  return new Date().toISOString().split('T')[0];
}

function fmtSyncTime(isoStr) {
  if (!isoStr || isoStr === 'None') return '—';
  const d = new Date(isoStr.replace(' ', 'T'));
  if (isNaN(d)) return isoStr;
  return d.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
}

export function AdminReportes() {
  const navigate = useNavigate();
  const [fecha, setFecha] = useState(isoToday);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  // Sync state
  const [fechaSync, setFechaSync] = useState(isoToday);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState('');
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    setLoadingStatus(true);
    try {
      const { data } = await axios.get(`${API}/admin/sync-mysql/status`);
      setStatus(data);
    } catch {
      setStatus({ conectado: false, error: 'No se pudo verificar' });
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleDownload = async () => {
    setError('');
    setDownloading(true);
    try {
      const response = await fetch(`${API}/reportes/diario?fecha=${fecha}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Error al generar reporte');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte_diario_${fecha}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Error al descargar reporte');
    } finally {
      setDownloading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setSyncError('');
    try {
      const { data } = await axios.post(`${API}/admin/sync-mysql/manual?fecha=${fechaSync}`);
      setSyncResult(data);
      await fetchStatus();
    } catch (err) {
      setSyncError(err.response?.data?.detail || 'Error al sincronizar');
    } finally {
      setSyncing(false);
    }
  };

  const formatDateDisplay = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('es-MX', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  };

  const ultimaSync = status?.tablas?.movimientos_pos?.ultima_sync;

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col" data-testid="admin-reportes-page">
      {/* Header */}
      <div className="bg-white px-4 py-3 border-b border-zinc-200">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg"
            data-testid="btn-back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-zinc-900">Reportes</h1>
        </div>
      </div>

      <div className="flex-1 px-4 py-6 space-y-4 pb-12">

        {/* ── Alertas de cuadre ── */}
        <button
          onClick={() => navigate('/admin/alertas')}
          className="w-full bg-white rounded-2xl border border-zinc-200 px-5 py-4 flex items-center gap-3 text-left hover:bg-zinc-50"
        >
          <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-red-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold text-zinc-900">Alertas de cuadre</h2>
            <p className="text-sm text-zinc-500">Inconsistencias por punto en un rango de fechas</p>
          </div>
          <ChevronRight className="w-5 h-5 text-zinc-400" />
        </button>

        {/* ── Reporte Diario ── */}
        <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-zinc-100 flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-zinc-900">Reporte Diario</h2>
              <p className="text-sm text-zinc-500">Resumen completo por punto de venta</p>
            </div>
          </div>

          <div className="px-5 py-5 space-y-5">
            <div className="bg-zinc-50 rounded-xl p-4 text-sm text-zinc-600 space-y-1">
              <p>El reporte incluye por cada punto de venta:</p>
              <ul className="list-disc list-inside space-y-0.5 text-zinc-500">
                <li>Ventas por producto (cantidad, precio, subtotal)</li>
                <li>Total de ingreso por ventas</li>
                <li>Entradas y salidas de inventario</li>
                <li>Gastos del dia con detalle</li>
                <li>Diferencia de efectivo (sobrante/faltante)</li>
                <li>Sobrante de cada pieza de pollo</li>
              </ul>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-zinc-600 mb-2">
                <Calendar className="w-4 h-4" />
                Fecha del reporte
              </label>
              <input
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className="w-full h-14 px-4 bg-white border border-zinc-300 rounded-xl text-lg font-medium text-zinc-900"
                data-testid="input-fecha-reporte"
              />
              <p className="text-sm text-zinc-400 mt-1.5 capitalize">{formatDateDisplay(fecha)}</p>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                {error}
              </div>
            )}

            <button
              onClick={handleDownload}
              disabled={downloading}
              className="w-full h-14 bg-emerald-600 text-white rounded-xl text-lg font-medium flex items-center justify-center gap-3 active:bg-emerald-700 disabled:opacity-60"
              data-testid="btn-download-reporte"
            >
              {downloading ? (
                <><Loader2 className="w-5 h-5 animate-spin" />Generando reporte...</>
              ) : (
                <><Download className="w-5 h-5" />Descargar Excel</>
              )}
            </button>

            <button
              onClick={() => navigate(`/admin/bitacora?fecha=${fecha}`)}
              className="w-full h-12 border border-zinc-300 text-zinc-700 rounded-xl text-sm font-medium flex items-center justify-center gap-2 active:bg-zinc-50"
              data-testid="btn-abrir-bitacora"
            >
              <ScrollText className="w-4 h-4" />
              Abrir bitácora del {fecha}
            </button>
          </div>
        </div>

        {/* ── Sincronización MySQL ── */}
        <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Database className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-zinc-900">Sincronización MySQL</h2>
                <p className="text-sm text-zinc-500">Reenviar datos a la base de datos externa</p>
              </div>
            </div>
            <button
              onClick={fetchStatus}
              disabled={loadingStatus}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-600"
              title="Refrescar estado"
            >
              <RefreshCw className={`w-4 h-4 ${loadingStatus ? 'animate-spin' : ''}`} />
            </button>
          </div>

          <div className="px-5 py-5 space-y-4">

            {/* Estado de conexión */}
            {loadingStatus ? (
              <div className="flex items-center gap-2 text-sm text-zinc-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                Verificando conexión...
              </div>
            ) : status ? (
              <div className={`flex items-start gap-3 p-3 rounded-xl border text-sm ${
                status.conectado
                  ? 'bg-green-50 border-green-200'
                  : 'bg-red-50 border-red-200'
              }`}>
                {status.conectado
                  ? <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                  : <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                }
                <div className="flex-1 min-w-0">
                  <p className={`font-medium ${status.conectado ? 'text-green-800' : 'text-red-700'}`}>
                    {status.conectado ? 'Conectado a MySQL' : 'Sin conexión a MySQL'}
                  </p>
                  {status.conectado && status.tablas && (
                    <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
                      {[
                        { key: 'ventas_pos',       label: 'Ventas' },
                        { key: 'movimientos_pos',  label: 'Movimientos' },
                        { key: 'cortes_pos',       label: 'Cortes' },
                        { key: 'clientes_pos',     label: 'Clientes' },
                        { key: 'bitacora_pos',     label: 'Bitácora' },
                      ].map(({ key, label }) => (
                        <div key={key} className="text-xs text-green-700">
                          <span className="font-medium">{label}:</span>{' '}
                          {status.tablas[key]?.total ?? '—'} registros
                        </div>
                      ))}
                    </div>
                  )}
                  {status.conectado && ultimaSync && (
                    <p className="text-xs text-green-600 mt-1.5">
                      Último sync: {fmtSyncTime(ultimaSync)}
                    </p>
                  )}
                  {!status.conectado && status.error && (
                    <p className="text-xs text-red-500 mt-0.5 truncate">{status.error}</p>
                  )}
                </div>
              </div>
            ) : null}

            {/* Fecha del sync */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-zinc-600 mb-2">
                <Calendar className="w-4 h-4" />
                Fecha a sincronizar
              </label>
              <input
                type="date"
                value={fechaSync}
                max={isoToday()}
                onChange={(e) => { setFechaSync(e.target.value); setSyncResult(null); setSyncError(''); }}
                className="w-full h-12 px-4 bg-white border border-zinc-300 rounded-xl text-base font-medium text-zinc-900"
              />
              <p className="text-sm text-zinc-400 mt-1 capitalize">{formatDateDisplay(fechaSync)}</p>
            </div>

            {/* Error de sync */}
            {syncError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {syncError}
              </div>
            )}

            {/* Resultados */}
            {syncResult && (
              <div className="bg-zinc-50 rounded-xl border border-zinc-200 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <p className="text-sm font-semibold text-zinc-800">Sincronización completada</p>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  {[
                    { label: 'Ventas', value: syncResult.totales?.ventas ?? 0 },
                    { label: 'Movimientos', value: syncResult.totales?.movimientos ?? 0 },
                    { label: 'Cortes', value: syncResult.totales?.cortes ?? 0 },
                  ].map(({ label, value }) => (
                    <div key={label} className="bg-white rounded-lg border border-zinc-200 py-2">
                      <p className="text-xl font-bold text-zinc-900">{value}</p>
                      <p className="text-xs text-zinc-500">{label}</p>
                    </div>
                  ))}
                </div>
                {syncResult.totales?.errores > 0 && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {syncResult.totales.errores} punto(s) con error
                  </p>
                )}
                {syncResult.por_punto?.length > 0 && (
                  <div className="space-y-1">
                    {syncResult.por_punto.map((p, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="text-zinc-600 truncate flex-1">{p.nombre}</span>
                        {p.ok ? (
                          <span className="text-green-600 font-medium ml-2 shrink-0">
                            {p.ventas} ventas · {p.movimientos} movs{p.corte ? ' · corte ✓' : ''}
                          </span>
                        ) : (
                          <span className="text-red-500 ml-2 shrink-0">Error</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Botón sync */}
            <button
              onClick={handleSync}
              disabled={syncing || !status?.conectado}
              className="w-full h-13 py-3.5 bg-blue-600 text-white rounded-xl text-base font-medium flex items-center justify-center gap-3 active:bg-blue-700 disabled:opacity-50"
            >
              {syncing ? (
                <><Loader2 className="w-5 h-5 animate-spin" />Sincronizando...</>
              ) : (
                <><RefreshCw className="w-5 h-5" />Sincronizar ahora</>
              )}
            </button>
            {!status?.conectado && !loadingStatus && (
              <p className="text-xs text-center text-zinc-400">
                Sin conexión a MySQL — verifica la configuración del servidor
              </p>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
